import type { ForwardedRef, ReactNode } from 'react';
import React, { forwardRef } from 'react';

import { CrossIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';
import { createPortal } from 'react-dom';

import st from './Popover.module.sass';
import { buildTestid } from '../../../lib/utils';

export interface PopoverProps extends WithDataTestid, WithClassNameProp {
  children: ReactNode;
  onClose: VoidFunction;
  rootProps?: React.ComponentPropsWithRef<'div'> | undefined;
  portalTarget?: HTMLElement | undefined;
}

const displayName = 'Popover';
const testid = buildTestid(displayName);

function Popover(
  {
    onClose,
    children,
    portalTarget = document.body,
    rootProps = undefined,
    className = undefined,
    'data-testid': dataTestId = undefined,
  }: PopoverProps,
  ref: ForwardedRef<HTMLDivElement>,
): React.JSX.Element | null {
  return createPortal(
    <div
      {...testid(dataTestId)}
      {...rootProps}
      className={cn(st.Popover, className, rootProps?.className)}
      ref={ref}
      role="dialog"
    >
      <Control.IconButton
        className={st.CloseButton}
        icon={<CrossIcon />}
        kind="secondary"
        onClick={onClose}
        size="small"
        variant="dimmed"
        {...testid('CloseButton', dataTestId)}
      />
      {children}
    </div>,
    portalTarget,
  );
}

Popover.displayName = displayName;

const PopoverWithRef = forwardRef(Popover);
export { PopoverWithRef as Popover };
